import { max, extent } from 'd3-array';
import { scaleTime } from 'd3-scale';
import { axisBottom } from 'd3-axis';
import { linearAxis } from '../../../factory/axes';

const parseDate = (str) => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export default function (props, response = []) {
  const { initialWidth, isMobile } = props;

  const data = Array.isArray(response) ? response : (response.data || []);

  const margins = {
    top: 30,
    right: isMobile ? 10 : 30,
    bottom: 35,
    left: 45,
  };

  const facetHeight = isMobile
    ? Math.round(initialWidth * 0.65)
    : Math.round(initialWidth * 0.35);

  const width = initialWidth - margins.right - margins.left;
  const height = facetHeight - margins.top - margins.bottom;

  // Parse dates and coerce counts
  const parsed = data.map(d => ({
    ...d,
    date: parseDate(d.date),
    count: +d.count,
  }));

  const israelData = parsed.filter(d => d.actor_clean === 'Israel');
  const hezbollahData = parsed.filter(d => d.actor_clean === 'Hezbollah');

  // Shared x domain across both actors — built directly to avoid factory's .nice() rounding
  const [minDate, maxDate] = extent(parsed, d => d.date);
  const x = scaleTime()
    .range([0, width])
    .domain([minDate || new Date(2023, 9, 7), maxDate || new Date()]);
  const xAxis = axisBottom;

  // Per-actor y scales on a shared pixel scale.
  // Israel's height is the reference; Hezbollah's height is proportional so that
  // one unit spans the same number of pixels in both facets.
  const israelMaxCount = max(israelData, d => d.count) || 1;
  const hezbollahMaxCount = max(hezbollahData, d => d.count) || 1;

  const israelYMax = Math.ceil(israelMaxCount / 50) * 50;
  const hezbollahYMax = Math.ceil(hezbollahMaxCount / 50) * 50;

  const israelHeight = height;
  const hezbollahHeight = Math.round(israelHeight * hezbollahYMax / israelYMax);

  const { axis: israelYAxis, scale: israelY } = linearAxis({
    orientation: 'left',
    range: [israelHeight, 0],
    domain: [0, israelYMax],
    nice: false,
  });

  const { axis: hezbollahYAxis, scale: hezbollahY } = linearAxis({
    orientation: 'left',
    range: [hezbollahHeight, 0],
    domain: [0, hezbollahYMax],
    nice: false,
  });

  // Bar width based on total date range
  const totalDays = minDate && maxDate
    ? (maxDate - minDate) / (1000 * 60 * 60 * 24) + 1
    : 1;
  const barWidth = Math.max(1, (width / totalDays) - 0.3);

  // Annotation dates
  // `facets` is preserved for potential future per-facet filtering.
  // `labelYMobile` overrides `labelY` on narrow viewports for stagger tuning.
  const annotations = [
    {
      date: new Date(2024, 8, 17), // Sept 17
      // endDate: new Date(2024, 8, 18), // Sept 18
      label: 'Israeli escalation following pager attacks',
      type: 'span',
      labelY: 12,
      labelYMobile: 12,
      labelSide: 'left',
      labelWidth: 80,
      facets: ['Israel'],
    },
    {
      date: new Date(2024, 10, 27), // Nov 27
      label: 'Israel and Hezbollah agree to ceasefire',
      type: 'line',
      labelY: 12,
      labelYMobile: 12,
      labelSide: 'right',
      labelWidth: 80,
      facets: ['Israel'],
    },
    {
      date: new Date(2023, 9, 8), // oct 8, 2023
      label: 'Hezbollah begins strikes on Israel',
      type: 'line',
      labelY: 12,
      labelYMobile: 12,
      labelSide: 'right',
      labelWidth: 80,
      facets: ['Hezbollah'],
    },
    {
      date: new Date(2026, 2, 2), // Mar 2, 2026
      label: 'Hezbollah resumes attacks after US and Israel launch attacks on Iran',
      type: 'line',
      labelY: 80,
      labelYMobile: 12,
      labelSide: 'left',
      labelWidth: 80,
      facets: ['Hezbollah'],
    },
    {
      date: new Date(2026, 3, 16), // April 16, 2026
      label: 'Ceasefire goes into effect',
      type: 'line',
      labelY: 12,
      labelYMobile: 30,
      labelSide: 'left',
      labelWidth: 80,
      facets: ['Hezbollah'],
    },
  ];

  return {
    israelData,
    hezbollahData,
    israelY,
    israelYAxis,
    israelHeight,
    israelYMax,
    hezbollahY,
    hezbollahYAxis,
    hezbollahHeight,
    hezbollahYMax,
    x,
    xAxis,
    width,
    margins,
    barWidth,
    annotations,
    isMobile,
  };
}
